import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, ExternalLink } from 'lucide-react';
import { SEO } from '../../../components/SEO';

/* â”€â”€â”€ Shared sub-components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const MapifyTopBar = () => (
  <div
    className="sticky top-0 z-50 border-b border-[#E2E8F0]"
    style={{ background: 'rgba(248,247,244,0.95)', backdropFilter: 'blur(20px)' }}
  >
    <div className="container-custom py-4 flex items-center justify-between">
      <Link
        to="/apps/mapify"
        className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
        Back to Mapify
      </Link>

      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ backgroundColor: '#1A73E8' }}>
          <MapPin size={15} color="#fff" />
        </div>
        <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Mapify</span>
      </div>

      <a
        href="#"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#95BF47' }}
      >
        Add to Shopify
        <ExternalLink size={12} />
      </a>
    </div>
  </div>
);

const MapifyFooter = () => (
  <div className="border-t border-[#E2E8F0] py-6">
    <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
        © {new Date().getFullYear()} Mapify · A Smart Solutions Product
      </p>
      <div className="flex items-center gap-4">
        <Link to="/apps/mapify/terms" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Terms</Link>
        <span className="text-[#E2E8F0]">·</span>
        <Link to="/apps/mapify/privacy" className="font-mono text-[0.55rem] tracking-widest text-[#1A73E8] uppercase">Privacy</Link>
      </div>
    </div>
  </div>
);

const Section = ({ num, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
  >
    <div className="flex items-start gap-4 mt-12 mb-3">
      <span className="font-mono text-[0.6rem] tracking-widest text-[#1A73E8] uppercase shrink-0 mt-1.5">{num}</span>
      <h2 className="font-display text-xl font-bold text-[#0F172A]">{title}</h2>
    </div>
    <div className="pl-8">{children}</div>
  </motion.div>
);

const P = ({ children }) => (
  <p className="text-[#374151] text-sm leading-relaxed mb-3">{children}</p>
);

const SubHeading = ({ children }) => (
  <h3 className="font-mono text-[0.65rem] tracking-widest text-[#374151] uppercase mb-2 mt-4">{children}</h3>
);

const DataPoint = ({ children }) => (
  <li className="text-[#374151] text-sm leading-relaxed flex items-start gap-2">
    <span className="w-1 h-1 rounded-full bg-[#1A73E8] shrink-0 mt-2" />
    {children}
  </li>
);

/* â”€â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export const MapifyPrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] flex flex-col">
      <SEO
        title="Mapify Privacy Policy"
        description="Privacy policy for the Mapify Shopify app by Smart Solutions."
        path="/apps/mapify/privacy"
      />
      <MapifyTopBar />

      <div className="flex-1">
        <div className="container-custom py-16 max-w-3xl mx-auto">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">Legal</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h1
              className="font-display font-bold text-[#0F172A] mb-3"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              Privacy Policy
            </h1>
            <p className="font-mono text-[0.65rem] tracking-widest text-[#9CA3AF] uppercase mb-8">
              Last updated: March 2025
            </p>
            <div className="divider-gold opacity-40" />
          </motion.div>

          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-8"
          >
            <P>
              This Privacy Policy explains how Mapify, operated by Smart Solutions, collects, uses, and protects data
              from merchants and their customers. We are committed to transparency and to handling all data responsibly.
            </P>
          </motion.div>

          {/* Sections */}
          <Section num="01" title="Who We Are">
            <P>
              Mapify is a Shopify app developed and operated by Smart Solutions. We are committed to protecting the
              privacy of both merchants and their customers.
            </P>
            <P>
              For privacy inquiries, contact us at:{' '}
              <a href="mailto:hello@smartsolution.ai" className="text-[#1A73E8] hover:underline">
                hello@smartsolution.ai
              </a>
            </P>
          </Section>

          <Section num="02" title="What Data We Collect">
            <SubHeading>From Customers (via the checkout map)</SubHeading>
            <P>
              When a customer uses the map and grants browser geolocation permission, the App collects:
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint>The customer's geographic coordinates (latitude and longitude) at the time the pin is placed</DataPoint>
              <DataPoint>The formatted street address derived from those coordinates via reverse geocoding</DataPoint>
            </ul>
            <P>
              This data is collected only if the customer explicitly grants browser location permission. If permission
              is denied or the customer skips the map, no location data is collected. We do not collect names, email
              addresses, payment information, or any other personal data from customers.
            </P>

            <SubHeading>From Merchants (store owners)</SubHeading>
            <P>When you install the App, we store:</P>
            <ul className="space-y-2 mb-4">
              <DataPoint>Your Shopify shop domain</DataPoint>
              <DataPoint>Your active billing plan ID and per-call rate</DataPoint>
              <DataPoint>API usage logs (shop domain, API type, cost units, success/error status, timestamp)</DataPoint>
            </ul>
            <P>
              Usage logs do not contain any customer personal data.
            </P>
          </Section>

          <Section num="03" title="How We Use the Data">
            <P>
              Customer location data (coordinates and derived address) is used solely to:
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint>Reverse-geocode the pin location into a human-readable address</DataPoint>
              <DataPoint>Save that address as order metafields on your Shopify store so you know the customer's intended delivery point</DataPoint>
            </ul>
            <P>Merchant data is used to:</P>
            <ul className="space-y-2 mb-4">
              <DataPoint>Authenticate API requests using Shopify session tokens (JWT)</DataPoint>
              <DataPoint>Calculate and process usage-based billing through the Shopify Billing API</DataPoint>
              <DataPoint>Display your usage dashboard within the App</DataPoint>
            </ul>
          </Section>

          <Section num="04" title="Data Storage & Retention">
            <P>
              Customer location data is stored as Shopify order metafields on your Shopify store — we do not retain
              it on our own servers beyond the processing request.
            </P>
            <P>
              Merchant session configuration (shop domain, billing plan) is stored in a PostgreSQL database hosted on
              Microsoft Azure (UAE North region).
            </P>
            <P>
              API usage logs are retained for the current billing period plus 90 days, after which they are automatically
              deleted. All data is stored within the Azure UAE North region.
            </P>
          </Section>

          <Section num="05" title="Third-Party Services">
            <P>
              The App uses the <strong className="font-medium text-[#0F172A]">Google Maps Platform</strong> (Maps JavaScript API and Geocoding API) to
              display maps and convert coordinates to addresses. Coordinate data is sent to Google's servers for
              geocoding. Please review{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1A73E8] hover:underline">
                Google's Privacy Policy
              </a>.
            </P>
            <P>
              The App is hosted on <strong className="font-medium text-[#0F172A]">Microsoft Azure</strong>. Please review{' '}
              <a href="https://privacy.microsoft.com" target="_blank" rel="noopener noreferrer" className="text-[#1A73E8] hover:underline">
                Microsoft's Privacy Statement
              </a>.
            </P>
          </Section>

          <Section num="06" title="Data Sharing">
            <P>
              We do not sell, rent, or trade your data or your customers' data. Data is shared only with Google for
              geocoding, as described above.
            </P>
            <P>
              We do not share data with any other third parties, advertisers, or analytics providers.
            </P>
          </Section>

          <Section num="07" title="Customer Rights">
            <P>
              Customers who wish to have their location data removed from an order may contact the merchant directly.
              Merchants can delete order metafields via the Shopify admin or the Shopify API. We encourage merchants
              to honour such requests promptly.
            </P>
          </Section>

          <Section num="08" title="Merchant Rights">
            <P>
              Merchants may request deletion of all data associated with their store (session config, usage logs) by
              contacting us at{' '}
              <a href="mailto:hello@smartsolution.ai" className="text-[#1A73E8] hover:underline">
                hello@smartsolution.ai
              </a>.
            </P>
            <P>
              Upon verified request, we will delete your store's data within 30 days, after which the App will stop
              functioning for your store.
            </P>
          </Section>

          <Section num="09" title="Security">
            <P>
              We use HTTPS for all API communication. Authentication is handled via Shopify's JWT session tokens —
              no cookies are set by the App.
            </P>
            <P>
              Our Azure infrastructure is protected by standard cloud security controls. We do not log or store full
              coordinate data on our servers beyond the geocoding request lifecycle.
            </P>
          </Section>

          <Section num="10" title="Changes to This Policy">
            <P>
              We may update this Privacy Policy from time to time. We will notify merchants of significant changes
              via the Shopify admin or email. The "last updated" date at the top of this page reflects the most
              recent revision.
            </P>
          </Section>

          <Section num="11" title="Contact Us">
            <P>For any privacy questions or data requests:</P>
            <div className="bg-white border border-[#E2E8F0] p-5 mt-2" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <p className="text-sm font-medium text-[#0F172A]">Smart Solutions</p>
              <p className="text-sm text-[#374151] mt-1">
                <a href="mailto:hello@smartsolution.ai" className="text-[#1A73E8] hover:underline">hello@smartsolution.ai</a>
              </p>
              <p className="text-sm text-[#374151]">Kuwait City, Kuwait</p>
            </div>
          </Section>

          <div className="divider-gold opacity-30 mt-16" />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
            <p className="font-mono text-[0.6rem] tracking-widest text-[#9CA3AF] uppercase">
              This policy was last updated in March 2025
            </p>
            <Link
              to="/apps/mapify/terms"
              className="font-mono text-[0.6rem] tracking-widest text-[#1A73E8] hover:underline uppercase"
            >
              View Terms &amp; Conditions →
            </Link>
          </div>
        </div>
      </div>

      <MapifyFooter />
    </div>
  );
};
